import { getProfileTheme } from './themes';
import type { ProfileLink } from './ProfileLinkEditor';
import type { ProfileFeedEntry } from './ProfileFeedEditor';

const ROLE_LABELS: Record<string, string> = {
  teacher: 'Teacher',
  sponsor: 'Sponsor',
  parent: 'Parent',
  student: 'Student',
  admin: 'Admin',
  superadmin: 'Admin',
};

interface ProfilePreviewProps {
  displayName: string;
  tagline: string;
  quoteText: string;
  quoteAttribution: string;
  themeKey: string;
  avatarUrl?: string | null;
  coverUrl?: string | null;
  links: ProfileLink[];
  feedEntries: ProfileFeedEntry[];
  role?: string | null;
}

export default function ProfilePreview({
  displayName,
  tagline,
  quoteText,
  quoteAttribution,
  themeKey,
  avatarUrl,
  coverUrl,
  links,
  feedEntries,
  role,
}: ProfilePreviewProps) {
  const theme = getProfileTheme(themeKey);
  const feed = feedEntries.filter((e) => e.title);

  return (
    <div className={`overflow-hidden rounded-3xl border ${theme.borderClass} ${theme.backgroundClass}`}>
      {coverUrl && (
        <div className="h-40 w-full overflow-hidden">
          <img src={coverUrl} alt="Cover" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="grid gap-6 p-6 md:grid-cols-[1.1fr,0.9fr]">
        <div className={`space-y-4 rounded-2xl p-5 shadow-sm ${theme.panelClass}`}>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white/80">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName || 'Profile'} className="h-full w-full object-cover" />
              ) : (
                <span className="text-xl font-semibold">{(displayName || '?')[0]}</span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold">{displayName || 'Your name'}</h3>
                {role && (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${theme.accentClass}`}>
                    {ROLE_LABELS[role] || role}
                  </span>
                )}
              </div>
              <p className="text-sm opacity-80">{tagline || 'Your tagline appears here.'}</p>
            </div>
          </div>
          {quoteText && (
            <blockquote className={`rounded-2xl border px-4 py-3 text-sm ${theme.borderClass}`}>
              &ldquo;{quoteText}&rdquo;
              {quoteAttribution && <footer className="mt-2 text-xs opacity-70">&mdash; {quoteAttribution}</footer>}
            </blockquote>
          )}
          {links.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {links.map((link) => (
                <span key={link.id} className={`rounded-full px-3 py-1 text-xs font-semibold ${theme.accentClass}`}>
                  {link.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          {feed.length > 0 && (
            <div className={`space-y-3 rounded-2xl p-4 ${theme.panelClass}`}>
              <h4 className="text-sm font-semibold uppercase tracking-wide opacity-80">Updates</h4>
              <div className="space-y-3">
                {feed.map((entry) => (
                  <article key={entry.id} className="space-y-2 rounded-2xl border border-white/50 bg-white/20 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{entry.tag || entry.type}</p>
                    <h5 className="text-base font-semibold">{entry.title}</h5>
                    {entry.body && <p className="text-sm opacity-80">{entry.body}</p>}
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
